import { Ajax } from '@newrelic/browser-agent/features/ajax';
import { GenericEvents } from '@newrelic/browser-agent/features/generic_events';
import { JSErrors } from '@newrelic/browser-agent/features/jserrors';
import { Metrics } from '@newrelic/browser-agent/features/metrics';
import { PageViewEvent } from '@newrelic/browser-agent/features/page_view_event';
import { PageViewTiming } from '@newrelic/browser-agent/features/page_view_timing';
import { Agent } from '@newrelic/browser-agent/loaders/agent';

import './newrelic.types';

const EU_BEACON = 'bam.eu01.nr-data.net';

// Stripe endpoints carry card PII in payloads — never let the agent harvest them.
const STRIPE_HOSTS = ['api.stripe.com', 'js.stripe.com', 'm.stripe.com', 'checkout.stripe.com'];

const ENABLED_FEATURES = [Ajax, JSErrors, Metrics, PageViewEvent, PageViewTiming, GenericEvents];

const resolveApiOrigin = (): string | null => {
	const baseUrl = process.env.REACT_APP_API_BASE_URL;
	if (!baseUrl) return null;
	try {
		return new URL(baseUrl).origin;
	} catch {
		return null;
	}
};

export const initNewRelic = (): void => {
	const licenseKey = process.env.REACT_APP_NEW_RELIC_LICENSE_KEY;
	const applicationID = process.env.REACT_APP_NEW_RELIC_APPLICATION_ID;
	const accountID = process.env.REACT_APP_NEW_RELIC_ACCOUNT_ID;
	const trustKey = process.env.REACT_APP_NEW_RELIC_TRUST_KEY;

	if (!licenseKey || !applicationID || !accountID || !trustKey) {
		// eslint-disable-next-line no-console
		console.info('[newrelic] disabled — env vars not configured');
		return;
	}

	const apiOrigin = resolveApiOrigin();

	const options = {
		info: {
			licenseKey,
			applicationID,
			beacon: EU_BEACON,
			errorBeacon: EU_BEACON,
			sa: 1,
		},
		init: {
			distributed_tracing: {
				enabled: true,
				cors_use_newrelic_header: true,
				cors_use_tracecontext_headers: true,
				allowed_origins: apiOrigin ? [apiOrigin] : [],
			},
			ajax: {
				deny_list: [EU_BEACON, ...STRIPE_HOSTS],
			},
			// Stripe redirect flows (Klarna/Affirm/Sunbit/Open Banking) land back on
			// our *.redirect routes with payment_intent_client_secret / setup_intent_client_secret
			// in the query string. Those values are real Stripe secrets and must not
			// reach NR — PageView pageUrl and JavaScriptError requestUri capture the
			// full URL otherwise.
			obfuscate: [
				{
					regex: /([?&])payment_intent_client_secret=[^&#]+/gi,
					replacement: '$1payment_intent_client_secret=REDACTED',
				},
				{
					regex: /([?&])setup_intent_client_secret=[^&#]+/gi,
					replacement: '$1setup_intent_client_secret=REDACTED',
				},
			],
			privacy: { cookies_enabled: false },
			session_replay: { enabled: false },
			session_trace: { enabled: false },
			logging: { enabled: false },
			soft_navigations: { enabled: false },
		},
		loader_config: {
			accountID,
			trustKey,
			agentID: applicationID,
			licenseKey,
			applicationID,
		},
		features: ENABLED_FEATURES,
	};

	// eslint-disable-next-line no-new
	new Agent(options);

	if (typeof window.newrelic !== 'undefined') {
		const version = process.env.REACT_APP_VERSION;
		const env = process.env.REACT_APP_ENVIRONMENT;
		if (version) window.newrelic.setApplicationVersion(version);
		if (env) window.newrelic.setCustomAttribute('env', env);
	}
};
